import { useEffect, useState } from "react";
import axios from "axios";
import { Instagram, Facebook, ExternalLink, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function InstagramTile({ p }) {
  const isVideo = p.media_type === "VIDEO";
  const img = p.thumbnail_url || p.media_url;
  if (isVideo && p.media_url) {
    return (
      <div className="group relative block aspect-square overflow-hidden bg-slate-900">
        <video src={p.media_url} poster={p.thumbnail_url} controls muted playsInline loop
               className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 uppercase tracking-widest pointer-events-none">Reel</div>
      </div>
    );
  }
  return (
    <a href={p.permalink} target="_blank" rel="noreferrer"
       className="group relative block aspect-square overflow-hidden bg-slate-900">
      {img && (
        <img src={img} alt={(p.caption || "").slice(0, 80)} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-white text-[11px] line-clamp-3">{p.caption || ""}</p>
      </div>
    </a>
  );
}

function FacebookTile({ p }) {
  const isVideo = p.type === "video";
  if (isVideo) {
    return (
      <div className="group relative block aspect-square overflow-hidden bg-slate-900">
        <iframe
          title={`fb-video-${p.id}`}
          src={`https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(p.permalink_url)}&show_text=false&width=400&height=400`}
          width="100%" height="100%"
          style={{ border: "none", overflow: "hidden", position: "absolute", inset: 0 }}
          scrolling="no" frameBorder="0" allowFullScreen
          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share" />
        <div className="absolute top-2 right-2 bg-black/70 text-white text-[10px] px-1.5 py-0.5 uppercase tracking-widest pointer-events-none flex items-center gap-1">
          <Play size={10} /> Video
        </div>
      </div>
    );
  }
  return (
    <a href={p.permalink_url} target="_blank" rel="noreferrer"
       className="group relative block aspect-square overflow-hidden bg-slate-900">
      {p.full_picture && (
        <img src={p.full_picture} alt={(p.message || "").slice(0, 80)} loading="lazy"
             className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
      )}
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <p className="text-white text-[11px] line-clamp-3">{p.message || ""}</p>
      </div>
    </a>
  );
}

export default function AutoFeeds() {
  const [data, setData] = useState({ instagram: [], facebook: [], facebook_page_url: "" });
  useEffect(() => {
    axios.get(`${API}/social/instagram`).then(r => setData(r.data || { instagram: [], facebook: [], facebook_page_url: "" })).catch(() => {});
  }, []);

  const hasIg = (data.instagram || []).length > 0;
  const hasFb = (data.facebook || []).length > 0;
  if (!hasIg && !hasFb) return null;

  return (
    <section id="autofeeds" className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Instagram feed */}
        {hasIg && (
          <>
            <div className="text-center mb-10">
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-wide uppercase" style={{ color: "#c41e3a" }}>
                Latest on Instagram
              </h2>
              <a href={`https://www.instagram.com/${data.instagram_handle || "wilsoncontractors"}`} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-[#c41e3a] mt-2">
                <Instagram size={14} /> @wilsoncontractors <ExternalLink size={12} />
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 md:gap-2 mb-16">
              {data.instagram.slice(0, 8).map(p => <InstagramTile key={p.id} p={p} />)}
            </div>
          </>
        )}

        {/* Facebook feed */}
        {hasFb && (
          <>
            <div className="text-center mb-10">
              <h2 className="font-display font-black text-3xl md:text-4xl tracking-wide uppercase" style={{ color: "#c41e3a" }}>
                On Facebook
              </h2>
              {data.facebook_page_url && (
                <a href={data.facebook_page_url} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-[#c41e3a] mt-2">
                  <Facebook size={14} /> View page <ExternalLink size={12} />
                </a>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-1 md:gap-2">
              {data.facebook.slice(0, 8).map(p => <FacebookTile key={p.id} p={p} />)}
            </div>
          </>
        )}

      </div>
    </section>
  );
}
